// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.
import * as Enums from "./enums";
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class GlobalSettings {
}
GlobalSettings.useAdvancedTextBlockTruncation = true;
GlobalSettings.useAdvancedCardBottomTruncation = false;
GlobalSettings.useMarkdownInRadioButtonAndCheckbox = true;
GlobalSettings.allowMarkForTextHighlighting = false;
GlobalSettings.alwaysBleedSeparators = false;
GlobalSettings.enableFullJsonRoundTrip = false;
GlobalSettings.displayInputValidationErrors = true;
GlobalSettings.allowPreProcessingPropertyValues = false;
GlobalSettings.setTabIndexAtCardRoot = true;
GlobalSettings.enableFallback = true;
GlobalSettings.useWebkitLineClamp = true;
GlobalSettings.allowMoreThanMaxActionsInOverflowMenu = false;
GlobalSettings.removePaddingFromContainersWithBackgroundImage = false;
GlobalSettings.resetInputsDirtyStateAfterActionExecution = true;
GlobalSettings.applets = {
    logEnabled: true,
    logLevel: Enums.LogLevel.Error,
    maximumRetryAttempts: 3,
    defaultTimeBetweenRetryAttempts: 3000,
    authPromptWidth: 400,
    authPromptHeight: 600,
    refresh: {
        mode: Enums.RefreshMode.Manual,
        timeBetweenAutomaticRefreshes: 3000,
        maximumConsecutiveAutomaticRefreshes: 3,
        allowManualRefreshesAfterAutomaticRefreshes: true
    }
};
export const ContentTypes = {
    applicationJson: "application/json",
    applicationXWwwFormUrlencoded: "application/x-www-form-urlencoded"
};
export class StringWithSubstitutions {
    constructor() {
        this._isProcessed = false;
    }
    getReferencedInputs(inputs, referencedInputs) {
        if (!referencedInputs) {
            throw new Error("The referencedInputs parameter cannot be null.");
        }
        if (this._original) {
            for (const input of inputs) {
                const matches = new RegExp("\\{{2}(" + input.id + ").value\\}{2}", "gi").exec(this._original);
                if (matches != null && input.id) {
                    referencedInputs[input.id] = input;
                }
            }
        }
    }
    substituteInputValues(inputs, contentType) {
        this._processed = this._original;
        if (this._original) {
            const regEx = /\{{2}([a-z0-9_$@]+).value\}{2}/gi;
            let matches;
            while ((matches = regEx.exec(this._original)) !== null && this._processed) {
                for (const key of Object.keys(inputs)) {
                    if (key.toLowerCase() === matches[1].toLowerCase()) {
                        const matchedInput = inputs[key];
                        let valueForReplace = "";
                        if (matchedInput.value) {
                            valueForReplace = matchedInput.value;
                        }
                        if (contentType === ContentTypes.applicationJson) {
                            valueForReplace = JSON.stringify(valueForReplace);
                            valueForReplace = valueForReplace.slice(1, -1);
                        }
                        else if (contentType === ContentTypes.applicationXWwwFormUrlencoded) {
                            valueForReplace = encodeURIComponent(valueForReplace);
                        }
                        this._processed = this._processed.replace(matches[0], valueForReplace);
                        break;
                    }
                }
            }
        }
        this._isProcessed = true;
    }
    getOriginal() {
        return this._original;
    }
    get() {
        if (!this._isProcessed) {
            return this._original;
        }
        else {
            return this._processed;
        }
    }
    set(value) {
        this._original = value;
        this._isProcessed = false;
    }
}
export class SpacingDefinition {
    constructor(top = 0, right = 0, bottom = 0, left = 0) {
        this.left = 0;
        this.top = 0;
        this.right = 0;
        this.bottom = 0;
        this.top = top;
        this.right = right;
        this.bottom = bottom;
        this.left = left;
    }
}
export class PaddingDefinition {
    constructor(top = Enums.Spacing.None, right = Enums.Spacing.None, bottom = Enums.Spacing.None, left = Enums.Spacing.None) {
        this.top = Enums.Spacing.None;
        this.right = Enums.Spacing.None;
        this.bottom = Enums.Spacing.None;
        this.left = Enums.Spacing.None;
        this.top = top;
        this.right = right;
        this.bottom = bottom;
        this.left = left;
    }
}
export class SizeAndUnit {
    static parse(input, requireUnitSpecifier = false) {
        const result = new SizeAndUnit(0, Enums.SizeUnit.Weight);
        if (typeof input === "number") {
            result.physicalSize = input;
            return result;
        }
        else if (typeof input === "string") {
            const regExp = /^([0-9]+)(px|\*)?$/g;
            const matches = regExp.exec(input);
            const expectedMatchCount = requireUnitSpecifier ? 3 : 2;
            if (matches && matches.length >= expectedMatchCount) {
                result.physicalSize = parseInt(matches[1]);
                if (matches.length === 3) {
                    if (matches[2] === "px") {
                        result.unit = Enums.SizeUnit.Pixel;
                    }
                }
                return result;
            }
        }
        throw new Error("Invalid size: " + input);
    }
    constructor(physicalSize, unit) {
        this.physicalSize = physicalSize;
        this.unit = unit;
    }
}
/**
 * Fast UUID generator, RFC4122 version 4 compliant.
 * @author Jeff Ward (jcward.com).
 * @license MIT license
 * @link http://stackoverflow.com/questions/105034/how-to-create-a-guid-uuid-in-javascript/21963136#21963136
 **/
/* eslint-disable @typescript-eslint/no-extraneous-class, @typescript-eslint/naming-convention, no-bitwise */
export class UUID {
    static generate() {
        const d0 = (Math.random() * 0xffffffff) | 0;
        const d1 = (Math.random() * 0xffffffff) | 0;
        const d2 = (Math.random() * 0xffffffff) | 0;
        const d3 = (Math.random() * 0xffffffff) | 0;
        return (UUID.lut[d0 & 0xff] +
            UUID.lut[(d0 >> 8) & 0xff] +
            UUID.lut[(d0 >> 16) & 0xff] +
            UUID.lut[(d0 >> 24) & 0xff] +
            "-" +
            UUID.lut[d1 & 0xff] +
            UUID.lut[(d1 >> 8) & 0xff] +
            "-" +
            UUID.lut[((d1 >> 16) & 0x0f) | 0x40] +
            UUID.lut[(d1 >> 24) & 0xff] +
            "-" +
            UUID.lut[(d2 & 0x3f) | 0x80] +
            UUID.lut[(d2 >> 8) & 0xff] +
            "-" +
            UUID.lut[(d2 >> 16) & 0xff] +
            UUID.lut[(d2 >> 24) & 0xff] +
            UUID.lut[d3 & 0xff] +
            UUID.lut[(d3 >> 8) & 0xff] +
            UUID.lut[(d3 >> 16) & 0xff] +
            UUID.lut[(d3 >> 24) & 0xff]);
    }
    static initialize() {
        for (let i = 0; i < 256; i++) {
            UUID.lut[i] = (i < 16 ? "0" : "") + i.toString(16);
        }
    }
}
UUID.lut = [];
UUID.initialize();
/* eslint-enable @typescript-eslint/no-extraneous-class, @typescript-eslint/naming-convention, no-bitwise */
//# sourceMappingURL=shared.js.map